import React, { useState, useMemo, useEffect } from "react";
import UserSidebar from "./UserSidebar";
import UserHeader from "./UserHeader";
import { useNavigate } from "react-router-dom";
import { FaSearch, FaSort, FaSortUp, FaSortDown } from "react-icons/fa";
import { IoIosInformationCircle } from "react-icons/io";
import { apiFetch } from "../api_call";
import "./Dashboard.css";

export default function DataProtectionInfo() {

  const [menuOpen, setMenuOpen] = useState(false);
  const [data, setData] = useState([]);
  const [selectedAct, setSelectedAct] = useState("");
  const [selectedCountry, setSelectedCountry] = useState("");
  const [loading, setLoading] = useState(true);
const [error, setError] = useState(null);

  const [searchTerm, setSearchTerm] = useState("");

  const [sortConfig, setSortConfig] = useState({
    key: "complianceId",
    direction: "asc",
  });

  const navigate = useNavigate();

  /* =====================================
     LOAD DATA FROM API USING LOCAL STORAGE
  ===================================== */

  useEffect(() => {

    const country = localStorage.getItem("selectedCountry");
    const act = localStorage.getItem("selectedAct");

    if (!country || !act) return;

    setSelectedCountry(country);
    setSelectedAct(act);

    loadCompliance(country, act);

  }, []);

 const loadCompliance = async (country, act) => {
  try {
    setLoading(true);
    setError(null);

    const res = await apiFetch(
      `/data-compliance/filter?country=${encodeURIComponent(country)}&act=${encodeURIComponent(act)}`
    );

    const records = res?.records || [];

    localStorage.setItem(
      "data_compliance_records",
      JSON.stringify(records)
    );

    const mapped = records.map((row) => ({
      complianceId: row.cmplst_compliance_key,
      act: row.cmplst_act,
      particular: row.cmplst_particular,
      description: row.cmplst_description || "",
      longDescription: row.cmplst_long_description,
      title: row.cmplst_title,
      country: row.cmplst_country,
      original: row,
    }));

    setData(mapped);
  } catch (err) {
    console.error("Error loading compliance:", err);
    setError("Failed to load compliance data.");
    setData([]);
  } finally {
    setLoading(false);
  }
};

  /* =====================================
     ADD BUTTON CLICK
  ===================================== */

  const handleAddClick = (row) => {

    // store selected row
    localStorage.setItem(
      "selectedComplianceRow",
      JSON.stringify(row)
    );

    // store module
    localStorage.setItem("module", "data-compliance");

    // store act + country again for safety
    localStorage.setItem("selectedAct", selectedAct);
    localStorage.setItem("selectedCountry", selectedCountry);

    navigate("/data-protection");

  };

  /* =====================================
     SEARCH
  ===================================== */

  const filteredData = useMemo(() => {

    if (!searchTerm) return data;

    return data.filter(
      (item) =>
        item.complianceId
          ?.toLowerCase()
          .includes(searchTerm.toLowerCase()) ||
        item.particular
          ?.toLowerCase()
          .includes(searchTerm.toLowerCase())
    );

  }, [data, searchTerm]);

  /* =====================================
     SORT
  ===================================== */

  const requestSort = (key) => {

    let direction = "asc";

    if (
      sortConfig.key === key &&
      sortConfig.direction === "asc"
    ) {
      direction = "desc";
    }

    setSortConfig({ key, direction });

  };

  const getSortIcon = (key) => {

    if (sortConfig.key !== key)
      return <FaSort className="sort-icon" />;

    return sortConfig.direction === "asc"
      ? <FaSortUp className="sort-icon" />
      : <FaSortDown className="sort-icon" />;

  };

  const sortedData = useMemo(() => {

    let sortable = [...filteredData];

    sortable.sort((a, b) => {

      const A = String(a[sortConfig.key] || "").toLowerCase();
      const B = String(b[sortConfig.key] || "").toLowerCase();

      if (A < B)
        return sortConfig.direction === "asc" ? -1 : 1;

      if (A > B)
        return sortConfig.direction === "asc" ? 1 : -1;

      return 0;

    });

    return sortable;

  }, [filteredData, sortConfig]);

  /* =====================================
     UI
  ===================================== */

  return (

    <div className="StatutoryInfo">

      <UserSidebar
        menuOpen={menuOpen}
        setMenuOpen={setMenuOpen}
      />

      <UserHeader
        menuOpen={menuOpen}
        setMenuOpen={setMenuOpen}
      />

      <div className="headSection">
        <div className="compliance-score">
          {selectedAct}
        </div>
      </div>

      <div className="table-header">

        <div></div>

        <div className="table-actions">

          <div className="search-box">

            <input
              placeholder="Search Compliance..."
              value={searchTerm}
              onChange={(e) =>
                setSearchTerm(e.target.value)
              }
            />

            <FaSearch className="search-icon" />

          </div>

        </div>

      </div>

      <table className="data-table">

        <thead>

          <tr>

            <th onClick={() => requestSort("complianceId")}>
              Compliance ID {getSortIcon("complianceId")}
            </th>

            <th onClick={() => requestSort("act")}>
              Act {getSortIcon("act")}
            </th>

            <th onClick={() => requestSort("particular")}>
              Particular {getSortIcon("particular")}
            </th>

            <th>Action</th>

          </tr>

        </thead>

       <tbody>
  {loading ? (
    <tr>
      <td colSpan="4" style={{ textAlign: "center", padding: "20px" }}>
        Loading...
      </td>
    </tr>
  ) : error ? (
    <tr>
      <td colSpan="4" style={{ textAlign: "center", color: "red", padding: "20px" }}>
        {error}
      </td>
    </tr>
  ) : sortedData.length === 0 ? (
    <tr>
      <td colSpan="4" style={{ textAlign: "center", padding: "20px" }}>
        No records found
      </td>
    </tr>
  ) : (
    sortedData.map((item, index) => (
      <tr key={index}>
        <td>{item.complianceId}</td>
        <td>{item.act}</td>

        <td>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "6px",
            }}
          >
            {item.particular}

            {item.description && (
              <IoIosInformationCircle
                title={item.description}
                style={{ cursor: "pointer" }}
              />
            )}
          </div>
        </td>

        <td>
          <button
            className="action-btn primary"
            onClick={() => handleAddClick(item)}
          >
            Add
          </button>
        </td>
      </tr>
    ))
  )}
</tbody>

      </table>

    </div>

  );

}
